import { ref, computed, watch } from 'vue'

const isDark = ref(false)

export function useDarkModeSafe() {
  // Función optimizada para aplicar dark mode sin observers problemáticos
  const applyDarkMode = () => {
    // Usar requestAnimationFrame para optimizar el rendimiento
    requestAnimationFrame(() => {
      if (isDark.value) {
        document.body.classList.add('dark-mode-active')
        document.documentElement.setAttribute('data-theme', 'dark')
      } else {
        document.body.classList.remove('dark-mode-active')
        document.documentElement.setAttribute('data-theme', 'light')
      }
    })
  }

  // Inicializar el modo oscuro desde localStorage
  const initializeDarkMode = () => {
    try {
      const stored = localStorage.getItem('darkModeActive')
      if (stored !== null) {
        isDark.value = JSON.parse(stored)
      } else {
        // Detectar preferencia del sistema con fallback seguro
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)')) {
          isDark.value = window.matchMedia('(prefers-color-scheme: dark)').matches
        }
      }
      applyDarkMode()
    } catch (error) {
      console.warn('Error inicializando dark mode:', error)
      isDark.value = false
      applyDarkMode()
    }
  }

  // Toggle del modo oscuro optimizado
  const toggleDarkMode = () => {
    isDark.value = !isDark.value
  }

  // Computed para clases CSS
  const themeClass = computed(() => isDark.value ? 'dark-mode-active' : 'light-mode')

  // Computed para iconos
  const themeIcon = computed(() => isDark.value ? 'ri-sun-line' : 'ri-moon-line')

  // Computed para texto del botón
  const themeText = computed(() => isDark.value ? 'Modo claro' : 'Modo oscuro')

  // Watch optimizado para persistir cambios (con debounce implícito)
  watch(isDark, (newValue) => {
    try {
      localStorage.setItem('darkModeActive', JSON.stringify(newValue))
      applyDarkMode()
    } catch (error) {
      console.warn('Error guardando preferencia dark mode:', error)
    }
  }, { immediate: false })

  // Sistema de escucha de cambios optimizado (solo si es necesario)
  let mediaQuery = null
  let mediaQueryListener = null
  
  try {
    if (window.matchMedia) {
      mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      mediaQueryListener = (e) => {
        // Solo cambiar si no hay preferencia manual guardada
        if (localStorage.getItem('darkModeActive') === null) {
          isDark.value = e.matches
        }
      }
      
      // Usar el método moderno si está disponible
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', mediaQueryListener)
      } else if (mediaQuery.addListener) {
        // Fallback para navegadores más antiguos
        mediaQuery.addListener(mediaQueryListener)
      }
    }
  } catch (error) {
    console.warn('Error configurando media query listener:', error)
  }

  // Función de cleanup para remover listeners
  const cleanup = () => {
    if (mediaQuery && mediaQueryListener) {
      try {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener('change', mediaQueryListener)
        } else if (mediaQuery.removeListener) {
          mediaQuery.removeListener(mediaQueryListener)
        }
      } catch (error) {
        console.warn('Error removiendo media query listener:', error)
      }
    }
  }

  return {
    isDark: computed(() => isDark.value),
    toggleDarkMode,
    initializeDarkMode,
    themeClass,
    themeIcon,
    themeText,
    cleanup
  }
}